import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Sparkles, Trash2, Check } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "../contexts/AuthContext";
import { useTeam } from "../contexts/TeamContext";
import { api } from "../lib/chalkApi";
import { UPGRADE_GATES } from "../lib/upgradeGates";
import {
  BottomSheet, ChalkActionButton, ChalkCard, ChalkIllustrationLines,
  DarkHeader, EmptyState, SectionTitle, UpgradeGateCard, WhiteHeader,
} from "../components/chalk/primitives";

const DUTY_PRESETS = ["Snack", "Field setup", "Scorekeeper", "Team photo", "Carpool"];

function humanWhen(iso) {
  if (!iso) return "TBD";
  const d = new Date(iso);
  return d.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function Volunteers() {
  const { user } = useAuth();
  const { activeTeam, teams, tier } = useTeam();
  const navigate = useNavigate();
  const [duties, setDuties] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [openRotation, setOpenRotation] = useState(false);

  const isGuardian = user.role === "guardian";
  const isCoachTier = tier.coach_tier === "chalkboard" || tier.coach_tier === "league_os";
  const Header = isGuardian ? WhiteHeader : DarkHeader;

  // Guardians: aggregate duties across all their teams. Coaches: only active team.
  const targetTeams = isGuardian ? teams : (activeTeam ? [activeTeam] : []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const all = [];
      const evMap = {};
      for (const t of targetTeams) {
        try {
          const [d, ev] = await Promise.all([
            api.listVolunteers(t.id),
            api.listEvents(t.id),
          ]);
          for (const x of d.duties || []) all.push({ ...x, team_id: t.id, team_name: t.name });
          for (const e of ev.events || []) evMap[e.id] = e;
        } catch { /* skip */ }
      }
      setDuties(all);
      setEvents(Object.values(evMap).sort((a, b) => new Date(a.local_time) - new Date(b.local_time)));
    } finally {
      setLoading(false);
    }
  // targetTeams is derived from {teams, activeTeam}; include the stable inputs.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTeam?.id, teams]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const grouped = useMemo(() => {
    const open = duties.filter((d) => d.status === "open");
    const mine = duties.filter((d) => d.assignee && d.assignee.id === user.id);
    const others = duties.filter((d) => d.status === "claimed" && (!d.assignee || d.assignee.id !== user.id));
    return { open, mine, others };
  }, [duties, user.id]);

  async function onClaim(d) {
    try {
      await api.claimDuty(d.id);
      toast.success("Got it — you're on the list.");
      loadAll();
    } catch (e) {
      toast.error(e.message || "Couldn't claim that one.");
    }
  }

  async function onDelete(d) {
    try {
      await api.deleteDuty(d.id);
      setDuties((arr) => arr.filter((x) => x.id !== d.id));
      toast.success("Duty removed.");
    } catch (e) {
      toast.error(e.message || "Couldn't remove that.");
    }
  }

  // Coach view — tier-gated upgrade card
  if (!isGuardian && !isCoachTier) {
    return (
      <>
        <Header eyebrow="Volunteers" title="Volunteers" subtitle={activeTeam?.name || ""} />
        <div style={{ padding: "0 18px 24px" }}>
          <UpgradeGateCard
            {...UPGRADE_GATES.volunteers}
            context={{ source_screen: "volunteers", current_tier: tier.coach_tier, team_id: activeTeam?.id }}
            ctaLabel="See Chalkboard options →"
            onUpgrade={async () => {
              navigate("/app/upgrade");
            }}
          />
        </div>
      </>
    );
  }

  return (
    <>
      <Header
        eyebrow="Volunteers"
        title={isGuardian ? "Sign up" : "Volunteers"}
        subtitle={isGuardian ? `${teams.length} team${teams.length === 1 ? "" : "s"}` : activeTeam?.name || ""}
      />
      <div style={{ padding: "0 18px 24px" }} data-testid="volunteers-page">
        {!isGuardian && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 14 }}>
            <ChalkActionButton
              testId="vol-add-duty"
              variant="ghost"
              label="Add duty"
              icon={<Plus size={14} aria-hidden="true" />}
              onClick={() => setOpenCreate(true)}
            />
            <ChalkActionButton
              testId="vol-rotate"
              variant="primary"
              label="Rotate snacks"
              icon={<Sparkles size={14} aria-hidden="true" />}
              onClick={() => setOpenRotation(true)}
            />
          </div>
        )}

        {loading ? (
          <div style={{ marginTop: 18, fontSize: 13, color: "var(--chalk-muted)" }}>Loading…</div>
        ) : duties.length === 0 ? (
          <EmptyState
            icon={<ChalkIllustrationLines />}
            headline={isGuardian ? "No duties yet." : "No volunteer duties yet."}
            body={isGuardian ? "Check back — coach will share the rotation soon." : "Add a duty or rotate snacks across the season."}
          />
        ) : (
          <>
            {isGuardian ? (
              <>
                {grouped.open.length > 0 && <SectionTitle label="Open — first tap wins" />}
                {grouped.open.map((d) => (
                  <DutyCard key={d.id} duty={d} actionLabel="Claim" onAction={() => onClaim(d)} testId={`duty-${d.id}`} />
                ))}
                {grouped.mine.length > 0 && <SectionTitle label="You're signed up" />}
                {grouped.mine.map((d) => (
                  <DutyCard key={d.id} duty={d} mine testId={`duty-${d.id}`} />
                ))}
                {grouped.others.length > 0 && <SectionTitle label="Rest of the season" />}
                {grouped.others.map((d) => (
                  <DutyCard key={d.id} duty={d} testId={`duty-${d.id}`} />
                ))}
              </>
            ) : (
              <>
                {grouped.open.length > 0 && <SectionTitle label="Open" />}
                {grouped.open.map((d) => (
                  <DutyCard key={d.id} duty={d} onDelete={() => onDelete(d)} testId={`duty-${d.id}`} />
                ))}
                {(grouped.mine.length + grouped.others.length) > 0 && <SectionTitle label="Assigned" />}
                {[...grouped.mine, ...grouped.others].map((d) => (
                  <DutyCard key={d.id} duty={d} onDelete={() => onDelete(d)} testId={`duty-${d.id}`} />
                ))}
              </>
            )}
          </>
        )}
      </div>

      <CreateDutySheet
        isOpen={openCreate}
        events={events}
        onDismiss={() => setOpenCreate(false)}
        onCreated={() => { setOpenCreate(false); loadAll(); }}
        teamId={activeTeam?.id}
      />

      <RotationSheet
        isOpen={openRotation}
        onDismiss={() => setOpenRotation(false)}
        onDone={(n) => {
          setOpenRotation(false);
          toast.success(`Rotated ${n} duties across the season.`);
          loadAll();
        }}
        teamId={activeTeam?.id}
      />
    </>
  );
}

function DutyCard({ duty, actionLabel, onAction, onDelete, mine, testId }) {
  return (
    <ChalkCard data-testid={testId}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 500, color: "var(--chalk-board)" }}>{duty.duty}</div>
          <div style={{ fontSize: 13, color: "var(--chalk-muted)", marginTop: 4 }}>
            {duty.event ? `${duty.event.title} · ${humanWhen(duty.event.start_utc)}` : "Anytime"}
            {duty.team_name ? ` · ${duty.team_name}` : ""}
          </div>
          {duty.assignee && (
            <div style={{ fontSize: 12, color: "var(--chalk-green)", marginTop: 6, display: "inline-flex", alignItems: "center", gap: 4 }}>
              <Check size={12} aria-hidden="true" />
              {mine ? "You" : `${duty.assignee.first_name} ${duty.assignee.last_name[0]}.`}
            </div>
          )}
          {!duty.assignee && (
            <div style={{ fontSize: 12, color: "var(--chalk-amber)", marginTop: 6, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Open
            </div>
          )}
        </div>
        {actionLabel && (
          <button
            data-testid={`${testId}-claim`}
            onClick={onAction}
            style={{
              background: "var(--chalk-board)", color: "var(--chalk-white)",
              border: "none", borderRadius: 999, padding: "6px 14px",
              fontSize: 12, cursor: "pointer",
            }}
          >
            {actionLabel}
          </button>
        )}
        {onDelete && (
          <button
            data-testid={`${testId}-delete`}
            onClick={onDelete}
            aria-label="Remove duty"
            style={{ background: "transparent", border: "none", color: "var(--chalk-muted)", cursor: "pointer" }}
          >
            <Trash2 size={14} aria-hidden="true" />
          </button>
        )}
      </div>
    </ChalkCard>
  );
}

function CreateDutySheet({ isOpen, onDismiss, onCreated, events, teamId }) {
  const [duty, setDuty] = useState("Snack");
  const [eventId, setEventId] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (isOpen) { setDuty("Snack"); setEventId(""); } }, [isOpen]);

  async function save() {
    if (!teamId) return;
    setSaving(true);
    try {
      await api.createDuty(teamId, { duty, event_id: eventId || null });
      onCreated && onCreated();
    } catch (e) {
      toast.error(e.message || "Couldn't add that duty.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <BottomSheet isOpen={isOpen} title="Add duty" onDismiss={onDismiss}>
      <div style={{ display: "grid", gap: 12 }} data-testid="create-duty-sheet">
        <div>
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--chalk-muted)", marginBottom: 6 }}>Duty</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {DUTY_PRESETS.map((p) => (
              <button
                key={p}
                onClick={() => setDuty(p)}
                data-testid={`duty-preset-${p.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                style={{
                  background: duty === p ? "var(--chalk-board)" : "#fff",
                  color: duty === p ? "var(--chalk-white)" : "var(--chalk-board)",
                  border: "0.5px solid var(--chalk-border)",
                  padding: "6px 12px", borderRadius: 999, fontSize: 12, cursor: "pointer",
                }}
              >{p}</button>
            ))}
          </div>
          <input
            data-testid="duty-input"
            value={duty}
            onChange={(e) => setDuty(e.target.value)}
            style={{
              marginTop: 8, width: "100%", border: "0.5px solid var(--chalk-border)",
              borderRadius: 10, padding: "10px 12px", fontSize: 14, outline: "none",
              background: "#FBF8F2", color: "var(--chalk-board)",
            }}
          />
        </div>
        <div>
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--chalk-muted)", marginBottom: 6 }}>Event (optional)</div>
          <select
            data-testid="duty-event"
            value={eventId}
            onChange={(e) => setEventId(e.target.value)}
            style={{
              width: "100%", border: "0.5px solid var(--chalk-border)",
              borderRadius: 10, padding: "10px 12px", fontSize: 14, background: "#FBF8F2",
            }}
          >
            <option value="">— Any time / season-long —</option>
            {events.filter((e) => e.status !== "cancelled").map((e) => (
              <option key={e.id} value={e.id}>{e.title} · {humanWhen(e.local_time)}</option>
            ))}
          </select>
        </div>
        <ChalkActionButton testId="duty-save" variant="primary" label="Add duty" loading={saving} onClick={save} />
      </div>
    </BottomSheet>
  );
}

function RotationSheet({ isOpen, onDismiss, onDone, teamId }) {
  const [duty, setDuty] = useState("Snack");
  const [limit, setLimit] = useState(8);
  const [running, setRunning] = useState(false);

  async function go() {
    if (!teamId) return;
    setRunning(true);
    try {
      const data = await api.bulkRotation(teamId, { duty, limit: Number(limit) });
      onDone && onDone(data.created || 0);
    } catch (e) {
      toast.error(e.message || "Couldn't rotate.");
    } finally {
      setRunning(false);
    }
  }

  return (
    <BottomSheet isOpen={isOpen} title="Rotate across the season" onDismiss={onDismiss}>
      <div style={{ display: "grid", gap: 12 }} data-testid="rotation-sheet">
        <div style={{ fontSize: 13, color: "var(--chalk-muted)" }}>
          One duty per upcoming event, rotated across the roster's primary guardians.
        </div>
        <div>
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--chalk-muted)", marginBottom: 6 }}>Duty</div>
          <input
            data-testid="rotation-duty"
            value={duty}
            onChange={(e) => setDuty(e.target.value)}
            style={{
              width: "100%", border: "0.5px solid var(--chalk-border)",
              borderRadius: 10, padding: "10px 12px", fontSize: 14, outline: "none",
              background: "#FBF8F2", color: "var(--chalk-board)",
            }}
          />
        </div>
        <div>
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--chalk-muted)", marginBottom: 6 }}>How many events</div>
          <input
            data-testid="rotation-limit"
            type="number" min={1} max={50}
            value={limit}
            onChange={(e) => setLimit(e.target.value)}
            style={{
              width: "100%", border: "0.5px solid var(--chalk-border)",
              borderRadius: 10, padding: "10px 12px", fontSize: 14, outline: "none",
              background: "#FBF8F2", color: "var(--chalk-board)",
            }}
          />
        </div>
        <ChalkActionButton testId="rotation-go" variant="primary" label={running ? "Rotating…" : "Rotate"} loading={running} onClick={go} />
      </div>
    </BottomSheet>
  );
}
